import React, { useState, useEffect } from 'react';
import { Period } from '../../services/periodCalendarService';
import { periodCalendarService } from '../../services/periodCalendarService';
import './PeriodFormModal.css';

interface PeriodFormModalProps {
  period: Period | null;
  userId: number;
  onClose: () => void;
  onSave: () => void;
  onError: (error: string) => void;
}

const PeriodFormModal: React.FC<PeriodFormModalProps> = ({
  period,
  userId,
  onClose,
  onSave,
  onError
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) {
      return '';
    }
    // Handles both ISO strings with timezones and YYYY-MM-DD strings
    const date = new Date(dateStr);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (period) {
      setStartDate(formatDateForInput(period.start_date));
      setEndDate(formatDateForInput(period.end_date));
      setNotes(period.notes || '');
    } else {
      const today = new Date().toISOString().split('T')[0];
      setStartDate(today);
      setEndDate('');
      setNotes('');
    }
  }, [period]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startDate) {
      onError('Start date is required');
      return;
    }

    if (endDate && new Date(endDate) < new Date(startDate)) {
      onError('End date must be after or equal to start date');
      return;
    }

    setSaving(true);
    try {
      if (period) {
        const response = await periodCalendarService.updatePeriod(period.id, {
          start_date: startDate,
          end_date: endDate || undefined,
          notes: notes || undefined
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to update period');
        }
      } else {
        const response = await periodCalendarService.createPeriod({
          user_id: userId,
          start_date: startDate,
          end_date: endDate || undefined,
          notes: notes || undefined
        });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to create period');
        }
      }
      
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error saving period:', err);
      onError(err.message || 'Failed to save period');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <div className="period-form-modal-overlay" onClick={handleClose}>
      <div className="period-form-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="period-form-modal-header">
          <h3>{period ? 'Edit Period' : 'Add New Period'}</h3>
          <button className="period-form-modal-close-btn" onClick={handleClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="period-form-modal-form">
          <div className="period-form-modal-group">
            <label htmlFor="start_date">Start Date *</label>
            <input
              type="date"
              id="start_date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              disabled={saving}
            />
          </div>
          
          <div className="period-form-modal-group">
            <label htmlFor="end_date">End Date</label>
            <input
              type="date"
              id="end_date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              disabled={saving}
            />
            <small className="period-form-modal-hint">Optional - leave empty if period is ongoing</small>
          </div>
          
          <div className="period-form-modal-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes about this period..."
              disabled={saving}
            />
          </div>
          
          <div className="period-form-modal-footer">
            <button
              type="button"
              onClick={handleClose}
              className="period-form-modal-cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="period-form-modal-submit-btn"
              disabled={saving}
            >
              {saving ? 'Saving...' : (period ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PeriodFormModal;

